import { RoleRepresentationDto } from '@bom-nextgen-keycloak/models';
import {
  selectPermissionGroupDetail,
  useAlertMessage,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  addGroupRoleMapping,
  ErrorMessage,
  FeaturePage,
  fetchGroupRoleAvailable,
  fetchGroupRoleComposite,
  fetchGroupRoleMapping,
  QUERY_KEY,
  updateGroupRoleMapping,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  intersection,
  ProtectedRouter,
  SelectBox,
  SelectBoxItem,
} from '@bom-nextgen-keycloak/web/shared/ui';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useGroupDetail } from '../../hooks/useGroupDetail';

const mapRoleToSelectBoxItem = (
  data: RoleRepresentationDto[]
): SelectBoxItem[] => {
  return data.map((val) => ({ id: val.id || '', name: val.name || '' }));
};

const GroupRoleMapping: FC = () => {
  const routerParams = useParams();
  const groupId = routerParams?.id || '';
  const resource = useResource();
  const { setAlertMessage } = useAlertMessage();
  const navigate = useNavigate();
  const [checked, setChecked] = useState<SelectBoxItem[]>([]);
  const queryClient = useQueryClient();
  const { groupDetail, isSubscription } = useGroupDetail(groupId);
  const { isEditRoleMappingTab } = useSelector(selectPermissionGroupDetail);

  useEffect(() => {
    if (isSubscription && groupDetail.level !== 4) {
      navigate(`/groups/${groupId}`);
    }
  }, []);

  const {
    isLoading,
    data: results = { roleMapping: [], roleAvailable: [], roleComposite: [] },
  } = useQuery(
    [QUERY_KEY.GROUP_ROLE_MAPPING, groupId],
    () =>
      Promise.all([
        fetchGroupRoleMapping(groupId, resource.clientId),
        fetchGroupRoleAvailable(groupId, resource.clientId),
        fetchGroupRoleComposite(groupId, resource.clientId),
      ]).then(([roleMapping, roleAvailable, roleComposite]) => ({
        roleMapping: mapRoleToSelectBoxItem(roleMapping) || [],
        roleAvailable: mapRoleToSelectBoxItem(roleAvailable) || [],
        roleComposite: mapRoleToSelectBoxItem(roleComposite) || [],
      })),
    {
      onError: (error: AxiosError<ErrorMessage>) => {
        const message =
          error.response?.data.message || 'Cannot get list group and group';
        setAlertMessage({
          message,
          typeStatusMessage: 'error',
          statusCode: error.response?.status,
        });
      },
    }
  );

  const roleAvailableChecked = intersection(checked, results.roleAvailable);
  const roleMappingChecked = intersection(checked, results.roleMapping);

  const addGroupRoleMutation = useMutation(addGroupRoleMapping, {
    onSuccess: (data) => {
      setAlertMessage({
        message: 'Assigned role(s) to a group',
        typeStatusMessage: 'success',
      });

      queryClient.invalidateQueries([QUERY_KEY.GROUP_ROLE_MAPPING, groupId]);
    },
    onSettled: async () => {
      setChecked([]);
    },
    onError: (error: AxiosError<ErrorMessage>) => {
      const message =
        error.response?.data.message || 'Cannot add group role mapping';
      setAlertMessage({
        message,
        typeStatusMessage: 'error',
        statusCode: error.response?.status,
      });
    },
  });

  const updateGroupRoleMutation = useMutation(updateGroupRoleMapping, {
    onSuccess: (data) => {
      setAlertMessage({
        message: 'Unassigned role(s) from a group',
        typeStatusMessage: 'success',
      });
      queryClient.invalidateQueries([QUERY_KEY.GROUP_ROLE_MAPPING, groupId]);
    },
    onSettled: async () => {
      setChecked([]);
    },
    onError: (error: AxiosError<ErrorMessage>) => {
      const message =
        error.response?.data.message || 'Cannot update group role mapping';
      setAlertMessage({
        message,
        typeStatusMessage: 'error',
        statusCode: error.response?.status,
      });
    },
  });

  const handleAddRoleMapping = () => {
    if (checked.length) {
      addGroupRoleMutation.mutate({
        groupId,
        clientId: resource.clientId,
        payload: { roles: checked },
      });
    }
  };

  const handleRemoveRoleMapping = () => {
    if (checked.length) {
      updateGroupRoleMutation.mutate({
        groupId,
        clientId: resource.clientId,
        payload: { roles: checked },
      });
    }
  };

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>
        <SelectBox
          title="Available roles"
          isloading={
            isLoading ||
            addGroupRoleMutation.isLoading ||
            updateGroupRoleMutation.isLoading
          }
          items={results.roleAvailable}
          checked={checked}
          handleChecked={(selected: SelectBoxItem[]) => setChecked(selected)}
        />
      </Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            disabled={
              roleAvailableChecked.length === 0 || !isEditRoleMappingTab
            }
            onClick={handleAddRoleMapping}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            disabled={roleMappingChecked.length === 0 || !isEditRoleMappingTab}
            onClick={handleRemoveRoleMapping}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        <SelectBox
          title="Assigned roles"
          isloading={
            isLoading ||
            addGroupRoleMutation.isLoading ||
            updateGroupRoleMutation.isLoading
          }
          items={results.roleMapping}
          checked={checked}
          handleChecked={(selected: SelectBoxItem[]) => setChecked(selected)}
        />
      </Grid>
      <Grid item>
        <SelectBox
          title="Effective roles"
          isloading={
            isLoading ||
            addGroupRoleMutation.isLoading ||
            updateGroupRoleMutation.isLoading
          }
          isShowOnly={true}
          items={results.roleComposite}
          checked={checked}
        />
      </Grid>
    </Grid>
  );
};

const WrappedGroupRoleMappingPage: FC = () => {
  const { canViewPage, canViewRoleMappingTab } = useSelector(
    selectPermissionGroupDetail
  );
  return (
    <ProtectedRouter
      isEditPage
      feature={FeaturePage.GROUP}
      canView={canViewPage && canViewRoleMappingTab}
      navigatePath="/groups"
    >
      <GroupRoleMapping />
    </ProtectedRouter>
  );
};

export { WrappedGroupRoleMappingPage };
